import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Autocomplete, BiContainer, Callout, ConfirmationModal, RadioButtonWithLabel, Spinner, Tooltip, Typography, useOverlayContext, getOptionLabel, getOptionSelected } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFetchContactAgreements } from '~/hooks/useGetAgreements';
import { useLocaleKeys } from '~/i18n';
import { handlePromise, handleRequest } from '~/infra/utils';
import { getInvoiceDateInfo } from '~/pages/AgreementMinisite/BillingHub/BillingHub.utils';
import { genericConnector } from '~/utils/react-hook-form.utils';
import { getGlobalBillServiceOptions } from '../../business-logic/billServiceSelector';
import { billingStagesTextsPath, createDefaultServiceItem, createEmptyChargeItem } from '../BillClientModal/v2/shared/Lines/lineItem.utils';
import ContactsSelector from './ContactsSelector/ContactsSelector';
import styles from './GlobalBillModal.module.scss';
import { BillingOption } from './GlobalBillModal.types';
import { AD_HOC_OPTION_VALUE, ContactAgreementState, classifyContactAgreementState, getServiceOptionSelected, getServiceOptionLabel } from './GlobalBillModal.utils';
export const GetGlobalBillModalTID = ()=>getTestIds({
        deprecatedTestIds: {
            GlobalBillModalTestIds: {
                NoAgreementCallout: 'global-bill-modal-no-agreement-callout',
                RadioButtons: 'global-bill-radio-buttons',
                AgreementsSelector: 'global-bill-modal-agreements-selector',
                ContinueButton: 'global-bill-modal-continue-button',
                ServicesSelector: 'agreement-manual-services-selector'
            }
        }
    });
export default function GlobalBillModal(param) {
    let { dismissModal, handleOpenBillClient, handleOpenAdHocBill } = param;
    const { t: texts } = useLocaleKeys('components.globalBillModal');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { api } = useStoreActions();
    const { AD_HOC_INVOICING } = useFeatureFlags();
    const { fetchAgreements, resetAgreements, agreements, adHocSummary, isOnlyOneAgreement, loading } = useFetchContactAgreements();
    const [selectedCheckboxOption, setSelectedCheckboxOption] = useState(BillingOption.outOfScope);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [globalBillCommonData, setGlobalBillCommonData] = useState(null);
    const [isCommonDataLoading, setIsCommonDataLoading] = useState(false);
    const [isCreatingAdHoc, setIsCreatingAdHoc] = useState(false);
    const [isAdHocSelected, setIsAdHocSelected] = useState(false);
    const overlayContext = useOverlayContext();
    const { t: stagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const contactAgreementState = AD_HOC_INVOICING ? classifyContactAgreementState(agreements, adHocSummary) : ContactAgreementState.OnlySignedAgreements;
    const adHocLabel = texts.invoicesWithoutAgreement();
    const adHocOption = useMemo(()=>({
            label: adHocLabel,
            value: AD_HOC_OPTION_VALUE
        }), [
        adHocLabel
    ]);
    const mixedOptions = useMemo(()=>contactAgreementState === ContactAgreementState.SignedAndAdHoc ? [
            ...agreements,
            adHocOption
        ] : [], [
        agreements,
        contactAgreementState,
        adHocOption
    ]);
    const { control, watch, setValue, handleSubmit } = useForm({
        mode: 'onSubmit'
    });
    const [selectedService, selectedAgreement, selectedContact] = watch([
        'selectedService',
        'selectedAgreement',
        'selectedContactId'
    ]);
    const { refresh } = useRelationship((selectedAgreement === null || selectedAgreement === void 0 ? void 0 : selectedAgreement.value) && selectedAgreement.value !== AD_HOC_OPTION_VALUE ? selectedAgreement.value : '');
    const showServicesSelector = selectedCheckboxOption === BillingOption.inAgreement && selectedAgreement && selectedContact;
    useEffect(()=>{
        if (!selectedAgreement || !selectedAgreement.value || selectedAgreement.value === AD_HOC_OPTION_VALUE) return;
        const selectedAgreementId = selectedAgreement.value;
        setIsCommonDataLoading(true);
        Promise.all([
            handlePromise(refresh()),
            handleRequest(api.relationship.getBillingControlData(selectedAgreementId)),
            handleRequest(api.invoiceDraft.getDates(selectedAgreementId))
        ]).then((param)=>{
            let [relationship, controlData, dates] = param;
            if (!relationship.success || !relationship.data || !controlData.success || !dates.success) {
                overlayContext.showErrorToast(texts.failedToLoadServices(), 'global-bill-failed-to-fetch');
                setIsCommonDataLoading(false);
                return;
            }
            setGlobalBillCommonData({
                relationship: relationship.data,
                controlData: controlData.data,
                dates: dates.data
            });
            setServiceOptions(getGlobalBillServiceOptions({
                services: relationship.data.services,
                controlData: controlData.data
            }));
            setIsCommonDataLoading(false);
        });
    }, [
        selectedAgreement
    ]);
    const onClearAgreement = ()=>{
        setSelectedCheckboxOption(BillingOption.outOfScope);
        setValue('selectedService', null, {
            shouldDirty: false
        });
        setServiceOptions([]);
        setIsAdHocSelected(false);
        setIsCommonDataLoading(false);
        setGlobalBillCommonData(null);
    };
    const handleChangeContact = (contactId)=>{
        onClearAgreement();
        setValue('selectedAgreement', null);
        resetAgreements();
        if (!contactId) {
            return;
        }
        fetchAgreements(contactId).then((result)=>{
            if ((result === null || result === void 0 ? void 0 : result.agreements.length) === 1 && !result.hasAdHoc) {
                setValue('selectedAgreement', result.agreements[0]);
            }
        });
    };
    const onOpenAddCharge = async (globalBillCommonData)=>{
        const { relationship, controlData, dates } = globalBillCommonData;
        const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
        const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
        const context = {
            relationshipId: relationship.id,
            totalCredits: controlData.totalCredits,
            invoiceDateInfo,
            earliestBillableDate: dates.earliestBillableDate(),
            manualServicesBilling: controlData.manualServices,
            controlData,
            defaultItem: createEmptyChargeItem()
        };
        handleOpenBillClient(context);
    };
    const onOpenBillService = async (globalBillCommonData, service)=>{
        const { relationship, controlData, dates } = globalBillCommonData;
        const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
        const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
        const defaultItem = createDefaultServiceItem({
            service,
            controlData,
            t: stagesTexts
        });
        if (!defaultItem) return;
        const billServiceData = {
            relationshipId: relationship.id,
            defaultItem,
            totalCredits: controlData.totalCredits,
            manualServicesBilling: controlData.manualServices,
            invoiceDateInfo,
            earliestBillableDate: dates.earliestBillableDate(),
            controlData
        };
        handleOpenBillClient(billServiceData);
    };
    const handleGlobalBillModalSubmit = async ()=>{
        if (contactAgreementState === ContactAgreementState.NoSignedAgreement && selectedContact) {
            setIsCreatingAdHoc(true);
            let relationshipId;
            try {
                ({ relationshipId } = await api.relationship.createAdHoc(selectedContact));
            } catch (unused) {
                overlayContext.showErrorToast(texts.createAdHocError(), 'global-bill-modal-create-adhoc-error-toast');
                setIsCreatingAdHoc(false);
                return;
            }
            setIsCreatingAdHoc(false);
            handleOpenAdHocBill(relationshipId);
            dismissModal();
            return;
        }
        if (contactAgreementState === ContactAgreementState.SignedAndAdHoc && isAdHocSelected && adHocSummary) {
            handleOpenAdHocBill(adHocSummary.id);
            dismissModal();
            return;
        }
        const selectedServiceId = selectedService === null || selectedService === void 0 ? void 0 : selectedService.id;
        const service = globalBillCommonData === null || globalBillCommonData === void 0 ? void 0 : globalBillCommonData.relationship.services.find((s)=>s.id === selectedServiceId);
        if (agreements.length === 0) {
            overlayContext.showErrorToast(texts.noAgreementsError(), 'global-bill-modal-no-agreements-error-toast');
            return;
        }
        if (selectedCheckboxOption === BillingOption.outOfScope && globalBillCommonData) {
            onOpenAddCharge(globalBillCommonData);
            dismissModal();
        } else if (selectedCheckboxOption === BillingOption.inAgreement && globalBillCommonData && service) {
            onOpenBillService(globalBillCommonData, service);
            dismissModal();
        } else {
            overlayContext.showErrorToast(texts.failedToOpenBillService(), 'open-bill-service-modal-error');
        }
    };
    const handleChooseBillingOption = (value)=>{
        setValue('selectedService', null);
        setSelectedCheckboxOption(value);
    };
    function renderAgreementsField() {
        if (loading) {
            return /*#__PURE__*/ _jsx(Spinner, {
                size: 32,
                centerBoxHeight: 36,
                centeredInBox: true
            });
        }
        if (contactAgreementState === ContactAgreementState.NoSignedAgreement) {
            return /*#__PURE__*/ _jsxs(Callout, {
                testId: "global-bill-modal-no-agreement-callout",
                intent: "info",
                withBorder: true,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        weight: "bold",
                        children: texts.noAgreementCalloutTitle()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        children: texts.noAgreementCalloutBody()
                    })
                ]
            });
        }
        const hasAdHoc = contactAgreementState === ContactAgreementState.SignedAndAdHoc;
        const dropdownOptions = hasAdHoc ? mixedOptions : agreements;
        return dropdownOptions.length > 1 || hasAdHoc ? /*#__PURE__*/ _jsx(Controller, {
            control: control,
            name: "selectedAgreement",
            rules: requiredValidation(true),
            render: (renderProps)=>/*#__PURE__*/ _jsx(Autocomplete, {
                    bi: {
                        name: 'agreement_selector'
                    },
                    selectOnFocus: true,
                    fullWidth: true,
                    testId: "global-bill-modal-agreements-selector",
                    textFieldProps: {
                        label: texts.agreementLabel(),
                        placeholder: texts.agreementOrAdHocPlaceholder(),
                        error: genericConnector(renderProps).showErrors
                    },
                    value: renderProps.field.value,
                    options: dropdownOptions,
                    getOptionLabel: getOptionLabel,
                    getOptionSelected: getOptionSelected,
                    dividers: hasAdHoc ? [
                        -1
                    ] : undefined,
                    onChange: (event, value)=>{
                        const newOption = value;
                        onClearAgreement();
                        setIsAdHocSelected((newOption === null || newOption === void 0 ? void 0 : newOption.value) === AD_HOC_OPTION_VALUE);
                        if (newOption !== selectedAgreement) {
                            renderProps.field.onChange(newOption);
                        }
                    }
                })
        }) : isOnlyOneAgreement ? null : /*#__PURE__*/ _jsx(Typography, {
            testId: "global-bill-modal-no-agreements",
            className: styles.noAgreementsText,
            variant: "paragraph1",
            skin: "secondary",
            children: texts.noAgreementsError()
        });
    }
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: "global_bill_modal",
        data: _object_spread({}, (selectedAgreement === null || selectedAgreement === void 0 ? void 0 : selectedAgreement.value) ? {
            relationshipId: selectedAgreement.value
        } : {}, selectedContact ? {
            clientId: selectedContact
        } : {}),
        children: /*#__PURE__*/ _jsx(ConfirmationModal, {
            title: texts.title(),
            testId: "global-bill-modal",
            primaryButtonTestId: "global-bill-modal-continue-button",
            textAlignCenter: false,
            onCloseButtonClick: dismissModal,
            primaryButtonLabel: commonButtons.continue(),
            secondaryButtonLabel: commonButtons.cancel(),
            onSecondaryButtonClick: dismissModal,
            primaryButtonLoading: isCommonDataLoading || isCreatingAdHoc || loading,
            onPrimaryButtonClick: contactAgreementState === ContactAgreementState.NoSignedAgreement && !!selectedContact || isAdHocSelected ? handleGlobalBillModalSubmit : handleSubmit(handleGlobalBillModalSubmit),
            bi: {
                primaryButton: {
                    name: 'continue'
                },
                secondaryButton: {
                    name: 'cancel'
                },
                closeButton: {
                    name: 'close'
                }
            },
            content: /*#__PURE__*/ _jsxs("div", {
                className: styles.globalBillModalContent,
                children: [
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "selectedContactId",
                        rules: requiredValidation(true),
                        render: (renderProps)=>/*#__PURE__*/ _jsx(ContactsSelector, {
                                disabled: loading,
                                selectedContactId: renderProps.field.value,
                                error: genericConnector(renderProps).showErrors,
                                onChange: (value)=>{
                                    renderProps.field.onChange(value);
                                    handleChangeContact(value);
                                }
                            })
                    }),
                    selectedContact && renderAgreementsField(),
                    selectedAgreement && !isAdHocSelected && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            selectedAgreement && !isAdHocSelected && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": "global-bill-radio-buttons",
                                className: styles.billingSelectorsWrapper,
                                children: [
                                    /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                                        bi: {
                                            name: 'bill_out_of_scope'
                                        },
                                        testId: "out-of-scope-radio",
                                        label: texts.billOutOfScope(),
                                        checked: selectedCheckboxOption === BillingOption.outOfScope,
                                        onChange: ()=>handleChooseBillingOption(BillingOption.outOfScope)
                                    }),
                                    /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                                        bi: {
                                            name: 'bill_in_agreement'
                                        },
                                        testId: "in-agreement-radio",
                                        label: texts.billServiceInAgreement(),
                                        checked: selectedCheckboxOption === BillingOption.inAgreement,
                                        onChange: ()=>handleChooseBillingOption(BillingOption.inAgreement)
                                    })
                                ]
                            }),
                            showServicesSelector && /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "selectedService",
                                rules: requiredValidation(true),
                                render: (renderProps)=>/*#__PURE__*/ _jsx(Autocomplete, {
                                        bi: {
                                            name: 'manual_service_selector'
                                        },
                                        className: styles.manualServicesSelector,
                                        selectOnFocus: true,
                                        testId: "agreement-manual-services-selector",
                                        textFieldProps: {
                                            placeholder: texts.chooseServicePlaceholder(),
                                            error: !!renderProps.fieldState.error
                                        },
                                        value: renderProps.field.value,
                                        getOptionSelected: getServiceOptionSelected,
                                        getOptionLabel: getServiceOptionLabel,
                                        options: serviceOptions,
                                        onBlur: renderProps.field.onBlur,
                                        noOptionsText: texts.noManualServicesAvailable(),
                                        getOptionDisabled: (option)=>option.disabledReason.isDisabled,
                                        renderOption: (option)=>option.disabledReason.isDisabled ? /*#__PURE__*/ _jsx(Tooltip, {
                                                placement: "right",
                                                title: option.disabledReason.reasonTooltip,
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.serviceOptionContent,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraph1",
                                                            children: option.name
                                                        }),
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraph1",
                                                            skin: "secondary",
                                                            children: option.costString
                                                        })
                                                    ]
                                                })
                                            }) : /*#__PURE__*/ _jsxs("div", {
                                                className: styles.serviceOptionContent,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        variant: "paragraph1",
                                                        children: option.name
                                                    }),
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        variant: "paragraph1",
                                                        skin: "secondary",
                                                        children: option.costString
                                                    })
                                                ]
                                            }),
                                        loading: isCommonDataLoading,
                                        onChange: (event, value)=>{
                                            const service = value ? value : null;
                                            if (service === selectedService) {
                                                return;
                                            }
                                            renderProps.field.onChange(service);
                                        },
                                        onInputChange: (e, value)=>{
                                            const service = serviceOptions.find((option)=>option.name === value);
                                            if (service) {
                                                renderProps.field.onChange(service);
                                            }
                                        }
                                    })
                            })
                        ]
                    })
                ]
            })
        })
    });
}
